import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, filter, take, tap, takeUntil } from 'rxjs';

import { UserService } from '../../../services/user.service';
import { MessagingService } from '../../../services/messaging.service';
import { ChatService } from '../../../services/chat.service';
import { ConversationService } from '../../../services/conversation.service';
import { Chat, Message, MessageAttachment } from '../../../models/message.model';
import { HighlightPipe } from '../../../pipes/highlight.pipe';
import { AttachmentPreviewDialog } from '../attachment-preview-dialog/attachment-preview-dialog';
import { HasPermissionDirective } from '../../../security/has-permission.directive';
import { USER_PERMISSIONS } from '../../../constants/permissions';
import { saveBlobAsFile } from '../../../helpers/file-download';
import { UserAvatar } from '../user-avatar/user-avatar';

/** Types allowed by the server's message-attachments endpoint — kept in
 *  sync with ALLOWED_ATTACHMENT_MIME_TYPES in message.controller.js. */
const ALLOWED_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
const MAX_ATTACHMENTS_PER_MESSAGE = 5;

interface PendingAttachment {
  file: File;
  status: 'uploading' | 'done' | 'error';
  attachment?: MessageAttachment;
}

@Component({
  selector: 'chat-widget',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatDividerModule,
    FormsModule,
    HighlightPipe,
    HasPermissionDirective,
    UserAvatar,
  ],
  templateUrl: './chat-widget.html',
  styleUrls: ['./chat-widget.css'],
})
export class ChatWidget implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ViewChild('attachmentInput') attachmentInputRef!: ElementRef<HTMLInputElement>;

  @Input() searchQuery = '';

  @Output() closed = new EventEmitter<void>();

  readonly attachmentAccept = ALLOWED_ATTACHMENT_ACCEPT;
  readonly maxAttachments = MAX_ATTACHMENTS_PER_MESSAGE;
  readonly permissions = USER_PERMISSIONS;
  pendingAttachments: PendingAttachment[] = [];
  isDraggingOver = false;
  private dragDepth = 0;

  constructor(
    private userService: UserService,
    private messagingService: MessagingService,
    private chatService: ChatService,
    private conversationService: ConversationService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
  ) {}

  currentMatchIndex = 0;
  matchNavDismissed = false;

  newMessage = '';
  selectedChat: Chat | null = null;
  currentUserId!: number;

  editingMessageId: number | null = null;
  editMessageText = '';

  replyingToMessage: Message | null = null;

  private shouldJump = false;
  private pendingJumpMessageId: number | null = null;
  highlightedMessageId: number | null = null;
  isNearBottom = true;
  showJumpToLatest = false;
  // Incremented right before every programmatic scrollTo()/scrollTop
  // assignment, decremented by the next onMessagesScroll() — see the
  // comment there for why. A counter rather than a single-shot boolean:
  // switching conversations in quick succession can issue several of these
  // before their native `scroll` events (fired asynchronously, one per
  // instant scroll) actually arrive, and a boolean only ever covers the
  // first of them — any later ones would land unprotected and get
  // misread as the user having scrolled away.
  private pendingProgrammaticScrolls = 0;
  private destroy$ = new Subject<void>();
  /** Tracks the last chat id seen from ChatService.selectedChat$, so a live
   *  update to the conversation already open (a new message, an edit) is
   *  distinguished from an actual change in which conversation is selected —
   *  only the latter resets match navigation. */
  private lastChatId: number | null = null;
  // Where the user was scrolled when they last left a conversation this
  // session, keyed by conversation id. Overwritten every time that
  // conversation is left again, so it always reflects the most recent
  // visit. Lives in component memory only — reset on reload/navigating
  // away from this page, not persisted.
  private lastScrollPositionByChat = new Map<number, { scrollTop: number; wasAtBottom: boolean }>();
  // Fed on every "still typing" signal; once it goes quiet for the debounce
  // window, we tell the other participant(s) typing has stopped — covers
  // the case where the user just abandons the input without clearing it or
  // sending, which an empty-input/send-triggered clear alone would miss.
  private typingStoppedSubject = new Subject<void>();

  ngOnInit(): void {
    this.setupTypingDebounce();

    if (this.userService.currentUser?.id) {
      this.currentUserId = this.userService.currentUser.id;
      this.chatService.initializeSocketListeners(this.currentUserId);
      this.subscribeToSelectedChat();
    } else {
      this.userService
        .getCurrentUser()
        .pipe(
          filter((user) => !!user && !!user.id),
          take(1),
          tap((user) => {
            this.currentUserId = user.id;
            this.chatService.initializeSocketListeners(this.currentUserId);
            this.subscribeToSelectedChat();
          }),
          takeUntil(this.destroy$),
        )
        .subscribe();
    }
  }

  private subscribeToSelectedChat(): void {
    this.chatService.selectedChat$.pipe(takeUntil(this.destroy$)).subscribe((chat) => {
      const isNewChatSelection = chat?.id !== this.lastChatId;

      // Snapshot where we're leaving off, before selectedChat gets
      // reassigned to the incoming chat below.
      if (this.selectedChat && this.selectedChat.id !== chat?.id) {
        this.saveScrollPosition(this.selectedChat.id);
      }

      this.selectedChat = chat;
      this.messagingService.setVisibleConversation(chat?.id ?? null);

      if (isNewChatSelection) {
        this.highlightedMessageId = null;
        this.matchNavDismissed = false;
        const ids = chat?.matchingMessageIds ?? [];
        this.currentMatchIndex = ids.length - 1;
        this.pendingJumpMessageId = ids[this.currentMatchIndex] ?? null;
        this.lastChatId = chat?.id ?? null;
      }

      // All template-bound state (showJumpToLatest, isNearBottom, etc.) is
      // finalized before the one detectChanges() call below — mutating it
      // afterward, in the same synchronous callback, is what
      // ExpressionChangedAfterItHasBeenCheckedError (NG0100) catches.
      let scrollSmooth = true;
      let shouldScrollNow = false;
      let restoreScrollTop: number | null = null;

      if (chat) {
        if (this.pendingJumpMessageId !== null) {
          this.shouldJump = true;
        } else if (isNewChatSelection) {
          const saved = this.lastScrollPositionByChat.get(chat.id);

          if (saved && !saved.wasAtBottom) {
            // Reading history when they left this conversation — put them
            // back where they were instead of yanking them down to the
            // bottom again.
            this.isNearBottom = false;
            this.showJumpToLatest = true;
            restoreScrollTop = saved.scrollTop;
          } else {
            // Either never visited this session, or was caught up at the
            // bottom when they left — jump straight to the (current)
            // bottom, no animation to watch.
            this.isNearBottom = true;
            this.showJumpToLatest = false;
            shouldScrollNow = true;
            scrollSmooth = false;
          }
        } else if (this.isNearBottom) {
          // Deliberately instant, not smooth: a `smooth` scrollTo() fires
          // one native `scroll` event per animation frame, but the
          // suppression below only ever covers a single event — so an
          // animated correction here could have several of its own
          // in-flight frames misread as "the user scrolled away"
          // mid-animation, intermittently landing short of the bottom.
          shouldScrollNow = true;
          scrollSmooth = false;
        } else {
          // A live update arrived while the user was scrolled up reading
          // older messages — don't yank them down, just surface the button.
          this.showJumpToLatest = true;
        }
      }

      this.cdr.detectChanges();

      if (restoreScrollTop !== null) {
        this.restoreScrollPosition(restoreScrollTop);
      } else if (shouldScrollNow) {
        this.scrolltoBottom(scrollSmooth);
      }
    });
  }

  sendMessage(): void {
    if (this.hasUploadingAttachments) return;

    const readyAttachments = this.pendingAttachments
      .filter((p): p is PendingAttachment & { attachment: MessageAttachment } => !!p.attachment)
      .map((p) => p.attachment);

    if ((!this.newMessage.trim() && !readyAttachments.length) || !this.selectedChat) return;

    this.chatService.sendMessage(
      this.newMessage,
      this.currentUserId,
      readyAttachments.length ? readyAttachments : undefined,
      this.replyingToMessage ?? undefined,
    );

    this.newMessage = '';
    this.pendingAttachments = [];
    // Sending always counts as "wanting to be at the bottom" — this message
    // isn't in selectedChat.messages yet (ChatService defers the optimistic
    // append), so without this, the live-update branch in the
    // selectedChat$ subscription would treat its arrival as "a message came
    // in while scrolled up, don't yank them down" instead of scrolling to
    // show it.
    this.isNearBottom = true;
    this.showJumpToLatest = false;
    this.replyingToMessage = null;
    this.cdr.detectChanges();

    // Clearing newMessage here is a component-driven model change, so the
    // composer's own (ngModelChange)="sendTyping(...)" binding never fires
    // for it — without this, the recipient's "typing..." would stay stuck
    // on until they typed something themselves.
    this.sendTyping(false);
  }

  /**
   * Triggers the hidden file input. The actual upload happens immediately
   * once a file is selected (onFilesSelected), not deferred to send —
   * mirrors PostDialog's imageHandler() upload-on-select behavior.
   */
  openAttachmentPicker(): void {
    this.attachmentInputRef.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.addFiles(files);
  }

  get canAttachFiles(): boolean {
    if (!this.selectedChat || this.selectedChat.is_disabled) return false;
    return this.userService.currentUser?.hasAnyPermission([this.permissions.MESSAGING.ATTACH_FILES]) ?? false;
  }

  onDragEnter(event: DragEvent): void {
    if (!this.canAttachFiles || event.dataTransfer?.types.includes('Files') === false) return;
    event.preventDefault();
    this.dragDepth++;
    this.isDraggingOver = true;
  }

  onDragOver(event: DragEvent): void {
    if (!this.canAttachFiles) return;
    event.preventDefault();
  }

  onDragLeave(event: DragEvent): void {
    if (!this.canAttachFiles) return;
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) this.isDraggingOver = false;
  }

  onFilesDropped(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth = 0;
    this.isDraggingOver = false;

    if (!this.canAttachFiles) return;
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.addFiles(files);
  }

  private addFiles(files: File[]): void {
    if (!files.length) return;

    const availableSlots = this.maxAttachments - this.pendingAttachments.length;
    files.slice(0, availableSlots).forEach((file) => this.uploadAttachment(file));
  }

  private uploadAttachment(file: File): void {
    const pending: PendingAttachment = { file, status: 'uploading' };
    this.pendingAttachments = [...this.pendingAttachments, pending];

    this.conversationService.uploadMessageAttachments([file]).subscribe({
      next: ([attachment]) => {
        pending.status = 'done';
        pending.attachment = attachment;
        this.cdr.detectChanges();
      },
      error: () => {
        pending.status = 'error';
        this.cdr.detectChanges();
      },
    });
  }

  removeAttachment(pending: PendingAttachment): void {
    this.pendingAttachments = this.pendingAttachments.filter((p) => p !== pending);
  }

  get canAddMoreAttachments(): boolean {
    return this.pendingAttachments.length < this.maxAttachments;
  }

  /** While true, Send is disabled — sending mid-upload would silently drop
   *  whichever attachment hadn't finished yet, since only ones with a
   *  resolved `.attachment` are bundled into the send. */
  get hasUploadingAttachments(): boolean {
    return this.pendingAttachments.some((p) => p.status === 'uploading');
  }

  isImageAttachment(attachment: MessageAttachment): boolean {
    return attachment.mime_type.startsWith('image/');
  }

  /** A lone photo sizes to its own aspect ratio instead of being forced
   *  into the fixed grid tile used for multi-image messages. */
  isSingleImageMessage(message: Message): boolean {
    return (message.attachments ?? []).filter((a) => this.isImageAttachment(a)).length === 1;
  }

  private isPreviewable(attachment: MessageAttachment): boolean {
    return this.isImageAttachment(attachment) || attachment.mime_type === 'application/pdf';
  }

  getAttachmentIcon(attachment: MessageAttachment): string {
    return attachment.mime_type === 'application/pdf' ? 'picture_as_pdf' : 'insert_drive_file';
  }

  /**
   * Images navigate amongst the other images in the same message; a PDF
   * previews on its own — mirroring the design's "only images page through
   * multiple, PDFs are one at a time" scope.
   */
  onAttachmentClick(message: Message, attachment: MessageAttachment): void {
    if (!this.isPreviewable(attachment)) {
      window.open(attachment.url, '_blank');
      return;
    }

    if (attachment.mime_type === 'application/pdf') {
      this.dialog.open(AttachmentPreviewDialog, {
        data: { attachments: [attachment], index: 0 },
        panelClass: 'attachment-preview-panel',
        autoFocus: 'dialog',
      });
      return;
    }

    const images = (message.attachments ?? []).filter((a) => this.isImageAttachment(a));
    const index = Math.max(
      images.findIndex((a) => a.url === attachment.url),
      0,
    );

    this.dialog.open(AttachmentPreviewDialog, {
      data: { attachments: images, index },
      panelClass: 'attachment-preview-panel',
      autoFocus: 'dialog',
    });
  }

  /**
   * Sets up the trailing debounce that automatically tells the other
   * participant(s) typing has stopped once the composer has gone quiet for
   * a few seconds — the leading "started typing" signal is sent immediately
   * from sendTyping() itself, on every keystroke.
   */
  private setupTypingDebounce(): void {
    this.typingStoppedSubject
      .pipe(debounceTime(3000), takeUntil(this.destroy$))
      .subscribe(() => this.sendTyping(false));
  }

  deleteMessage(message: Message): void {
    if (!this.selectedChat?.userId) return;

    this.messagingService.deleteMessage(message.id, message.conversation_id);
  }

  sendTyping(isTyping: boolean): void {
    if (!this.selectedChat) return;

    this.messagingService.sendTyping({
      conversation_id: this.selectedChat.id,
      sender_id: this.currentUserId,
      recipient_id: this.selectedChat.userId,
      isTyping,
    });

    if (isTyping) {
      // Restarts the "gone quiet" debounce timer on every keystroke, so a
      // false is sent automatically a few seconds after the last one.
      this.typingStoppedSubject.next();
    }
  }

  /**
   * Names whoever is currently typing in a chat, resolved from
   * typingUserIds via that chat's members list — works the same for direct
   * and group conversations, since a direct chat's members array already
   * includes the other participant. Returns '' when nobody is typing.
   * Mirrors messages.ts/counselor-msg.ts's getTypingLabel().
   * @param {Chat} chat - The chat to summarize typing activity for.
   * @returns {string}
   */
  getTypingLabel(chat: Chat): string {
    const ids = chat.typingUserIds ?? [];

    if (!ids.length) {
      return '';
    }

    const names = ids
      .map((id) => chat.members?.find((member) => member.id === id)?.name)
      .filter((name): name is string => !!name);

    if (!names.length) {
      return '';
    }

    if (names.length === 1) {
      return `${names[0]} is typing...`;
    }

    if (names.length === 2) {
      return `${names[0]} and ${names[1]} are typing...`;
    }

    return `${names[0]}, ${names[1]} +${names.length - 2} are typing...`;
  }

  startEditMessage(message: Message): void {
    this.editingMessageId = message.id;
    this.editMessageText = message.content;
  }

  startReply(message: Message): void {
    if (message.is_deleted || message.is_system) return;
    this.replyingToMessage = message;
  }

  cancelReply(): void {
    this.replyingToMessage = null;
  }

  /**
   * Scrolls to and briefly highlights a message elsewhere in the thread —
   * used both by clicking a quoted reply snippet and (already) by jumping
   * to a search match.
   */
  jumpToMessage(messageId: number): void {
    this.pendingJumpMessageId = messageId;
    this.shouldJump = true;
  }

  cancelEditMessage(): void {
    this.editingMessageId = null;
    this.editMessageText = '';
  }

  saveEditedMessage(message: Message): void {
    const newContent = this.editMessageText.trim();

    if (!newContent || newContent === message.content) {
      this.cancelEditMessage();
      return;
    }

    this.messagingService.editMessage(message.id, message.conversation_id, newContent);

    this.cancelEditMessage();
  }

  close(): void {
    this.closed.emit();
  }

  /**
   * Fetches a plain-text transcript of the open conversation and saves it as
   * a file. Gated behind MESSAGING.EXPORT_TRANSCRIPT — the download button
   * itself is hidden without that permission, but the request would also be
   * rejected server-side.
   */
  downloadTranscript(): void {
    if (!this.selectedChat) return;

    const chat = this.selectedChat;
    this.conversationService.downloadTranscript(chat.id).subscribe({
      next: (blob) => saveBlobAsFile(blob, this.buildTranscriptFilename(chat)),
      error: () => this.snackBar.open('Failed to download transcript.', 'Dismiss', { duration: 5000 }),
    });
  }

  private buildTranscriptFilename(chat: Chat): string {
    const safeName = chat.name.replace(/[^a-z0-9]+/gi, '_');
    const date = new Date().toISOString().slice(0, 10);
    return `transcript_${safeName}_${date}.txt`;
  }

  formatTime(date: Date | string): string {
    return new Date(date).toLocaleDateString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Returns uppercase initials from a full name string.
   * @param {string} name - Full name to derive initials from.
   * @returns {string} e.g. 'Jean P.' → 'JP'
   */
  getInitials(name: string): string {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  }

  getSenderName(message: Message): string {
    const sender = message.sender;

    if (!sender) {
      return '';
    }

    return `${sender.preferredName ?? sender.first_name ?? ''} ${sender.last_name ?? ''}`.trim();
  }

  getReplyQuoteSenderName(replyTo: NonNullable<Message['replyTo']>): string {
    const sender = replyTo.sender;

    if (!sender) {
      return '';
    }

    return `${sender.preferredName ?? sender.first_name ?? ''} ${sender.last_name ?? ''}`.trim();
  }

  /** Matches the '📎 Attachment' fallback already used for attachment-only
   *  messages in the conversation list preview and the send-broadcast
   *  preview payload, for a consistent look across the app. */
  getReplyQuoteSnippet(replyTo: NonNullable<Message['replyTo']>): string {
    if (replyTo.is_deleted) {
      return 'This message was deleted';
    }

    const content = replyTo.content?.trim();

    if (!content) {
      return '📎 Attachment';
    }

    return content.length > 80 ? `${content.slice(0, 80)}…` : content;
  }

  ngAfterViewChecked(): void {
    if (this.shouldJump) {
      this.scrollToMessage(this.pendingJumpMessageId);
      this.shouldJump = false;
      this.pendingJumpMessageId = null;
    }
  }

  /**
   * The message holding the match the user is currently parked on, so the
   * template can highlight it more strongly than the other matches.
   * @returns {number | null}
   */
  get activeMatchMessageId(): number | null {
    const ids = this.selectedChat?.matchingMessageIds ?? [];
    return ids[this.currentMatchIndex] ?? null;
  }

  /**
   * Hides the match navigation popup. Deliberately leaves the search query and
   * the match results untouched — this only dismisses the UI.
   * @returns {void}
   */
  closeSearchPopup(): void {
    this.matchNavDismissed = true;
  }

  private scrollToMatch(chat: Chat, delta: number): void {
    const ids = chat.matchingMessageIds;
    if (!ids?.length) return;

    //enables wrap around
    this.currentMatchIndex = (this.currentMatchIndex + delta + ids.length) % ids.length;
    this.pendingJumpMessageId = ids[this.currentMatchIndex];

    this.shouldJump = true;
  }

  jumpPrev(chat: Chat): void {
    this.scrollToMatch(chat, -1);
  }

  jumpNext(chat: Chat, targetMessageId?: number): void {
    this.scrollToMatch(chat, 1);
  }

  /**
   * Scrolls a specific message into view and flags it so the template can
   * highlight it briefly.
   * @param {number | null} messageId - The message to scroll to.
   * @returns {void}
   */
  private scrollToMessage(messageId: number | null): void {
    if (messageId === null) return;

    const element = document.getElementById(`message-${messageId}`);
    if (!element) {
      this.scrolltoBottom();
      return;
    }

    element.scrollIntoView({ block: 'center' });

    // Clearing first removes the `highlighted` class (needed even when
    // re-jumping to the same message we last highlighted — otherwise
    // re-assigning the same id is a no-op for the class binding). But the
    // removal and re-add below both happen in this same synchronous tick,
    // before the browser ever gets to a paint — so without forcing a
    // reflow in between, the render pipeline never actually observes the
    // "removed" state as a separate step, and a CSS animation tied to the
    // class being added doesn't replay. Reading offsetHeight forces that
    // reflow now, while the class is still off.
    this.highlightedMessageId = null;
    this.cdr.detectChanges();
    void element.offsetHeight;
    this.highlightedMessageId = messageId;
    this.cdr.detectChanges();
  }

  private scrolltoBottom(smooth = true): void {
    try {
      const el = this.scrollContainer.nativeElement;
      this.pendingProgrammaticScrolls++;
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    } catch (e) {
      console.warn('Scroll failed:', e);
    }
  }

  // Snapshots where the user was scrolled in a conversation right before
  // leaving it, so we can put them back there if they return.
  private saveScrollPosition(chatId: number): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    this.lastScrollPositionByChat.set(chatId, {
      scrollTop: el.scrollTop,
      wasAtBottom: this.isNearBottom,
    });
  }

  private restoreScrollPosition(scrollTop: number): void {
    try {
      const el = this.scrollContainer.nativeElement;
      this.pendingProgrammaticScrolls++; // reuses the existing guard, same as scrolltoBottom()
      el.scrollTop = scrollTop;
    } catch (e) {
      console.warn('Scroll restore failed:', e);
    }
  }

  /**
   * Tracks how far the user has scrolled from the bottom of the open
   * conversation, so a live update only auto-scrolls when they were already
   * caught up, and the jump-to-latest button only shows when they aren't.
   *
   * The native `scroll` event this responds to fires asynchronously, so a
   * scrollTo() we just issued ourselves (e.g. jumping to the bottom when a
   * conversation opens) can resolve *after* an attachment has already grown
   * the layout underneath it — landing scrollTop short of the new
   * scrollHeight and making this look like the user scrolled away, when
   * really our own scroll just hadn't caught up yet. One scroll-tracking
   * pass is skipped per outstanding programmatic scroll so those transient
   * snapshots never overwrite isNearBottom.
   */
  onMessagesScroll(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;

    if (this.pendingProgrammaticScrolls > 0) {
      this.pendingProgrammaticScrolls--;
      return;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.isNearBottom = distanceFromBottom < 150;
    this.showJumpToLatest = !this.isNearBottom;
  }

  /**
   * Attachment thumbnails have no height until their image data finishes
   * downloading, so a scrollTo() issued before that can settle short of
   * the true bottom — and unlike a single corrective re-scroll, multiple
   * attachments can each finish loading independently, well after any one
   * correction already ran (e.g. on a slow connection). Re-running the
   * scroll on every image load closes that gap regardless of how many
   * images there are or when they finish. Always instant (not smooth), so
   * it's covered by the same single-shot suppression as every other
   * scrolltoBottom() call above — no separate handling needed. Gated on
   * isNearBottom so a late-loading image from earlier history doesn't
   * yank down a user who has since scrolled up to read.
   */
  onAttachmentImageLoad(): void {
    if (this.isNearBottom) this.scrolltoBottom(false);
  }

  /**
   * Scrolls down to the newest message and dismisses the jump-to-latest button.
   */
  jumpToLatest(): void {
    this.scrolltoBottom(true);
    this.isNearBottom = true;
    this.showJumpToLatest = false;
  }

  ngOnDestroy(): void {
    // This component only exists while the widget's pane is actually shown
    // (*ngIf in messaging-widget.html), so being destroyed means the
    // conversation just went off-screen — tell the server so a new message
    // notifies the user again rather than being silently skipped.
    this.messagingService.setVisibleConversation(null);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
